import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { createAction, createFeatureSelector, createReducer, createSelector, on, props } from '@ngrx/store';
import { catchError, defaultIfEmpty, of, switchMap } from 'rxjs';
import { saveAs } from 'file-saver-es';
import { EdicaoPublicadaService } from 'src/app/services/edicao-publicada.service';
import { CommonMessages } from 'src/app/utils/common.messages';
import { toastShow } from 'src/app/stateManagement/actions/toast.action';
import { ToastModel, ToastType } from 'src/app/domain/models/toast.model';

const actionName = '[Baixar PDF Edição]';

export const baixarPdfCadernoEdicao = createAction(`${actionName} - Baixar`, props<{ cadernoEdicaoId: number; }>());
export const baixarPdfCadernoEdicaoSuccess = createAction(`${actionName} - Sucesso`);
export const baixarPdfCadernoEdicaoFailure = createAction(`${actionName} - Falha`, props<{ erro: string }>());
export const baixarPdfCadernoEdicaoFinish = createAction(`${actionName} - Concluir`);

export interface BaixarPdfEdicaoState {
  error: string | null;
  carregando: boolean;
}

const initialState: BaixarPdfEdicaoState = { error: null, carregando: false };

// -- Reducer
export const baixarPdfEdicaoReducer = createReducer(
  initialState,
  on(baixarPdfCadernoEdicao, state => ({ ...state, error: null, carregando: true })),
  on(baixarPdfCadernoEdicaoSuccess, state => ({ ...state, error: null, carregando: false })),
  on(baixarPdfCadernoEdicaoFailure, (state, { erro }) => ({ ...state, error: erro, carregando: false })),
  on(baixarPdfCadernoEdicaoFinish, () => initialState)
);

// -- Selector
export const selectBaixarPdfCadernoEdicaoState = createFeatureSelector<BaixarPdfEdicaoState>('baixarPdfEdicao');
export const selectCarregandoBaixarPdfCadernoEdicao = createSelector(selectBaixarPdfCadernoEdicaoState, state => state.carregando);
export const selectErrosBaixarPdfCadernoEdicao = createSelector(selectBaixarPdfCadernoEdicaoState, state => state.error);

// -- Effect
const falhaComToast = () => [
  baixarPdfCadernoEdicaoFailure({ erro: CommonMessages.EdicaoHtml.BaixarCadernoEdicaoErro }),
  toastShow({ toastModel: new ToastModel(CommonMessages.Alert, CommonMessages.EdicaoHtml.BaixarCadernoEdicaoErro, ToastType.Warning) })
];

const extrairNomeArquivo = (contentDisposition: string | null): string | undefined => {
  if (!contentDisposition) 
    return undefined;

  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (utf8?.[1]) 
    return decodeURIComponent(utf8[1].trim());

  const ascii = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return ascii?.[1]?.trim();
};


@Injectable()
export class BaixarPdfEdicaoEffects {
  baixarPdfEdicao$ = createEffect(() => this.actions$.pipe(
    ofType(baixarPdfCadernoEdicao),
    switchMap(({ cadernoEdicaoId }) =>
      this.edicaoPublicadaService.ObterPdfCadernoEdicao(cadernoEdicaoId).pipe(
        defaultIfEmpty(null),
        switchMap(response => {
          if (!response?.body || response.body.size === 0)
            return of(...falhaComToast());

          saveAs(response.body, extrairNomeArquivo(response.headers.get('Content-Disposition')));
          return of(baixarPdfCadernoEdicaoSuccess());
        }),
        catchError(() => of(...falhaComToast()))
      )
    )
  ));

  constructor(private readonly actions$: Actions, private readonly edicaoPublicadaService: EdicaoPublicadaService) {}
}
